import { Navigate, Route, useLocation } from 'react-router-dom';
import { WAREHOUSE_HOME } from '../../layouts/areaSwitch';
import { SellerLayout } from '../../layouts/SellerLayout';
import { Account } from '../../pages/Account/Account';
import { NotFound } from '../../pages/NotFound/NotFound';
import { Dashboard } from './pages/Dashboard/Dashboard';
import { Deliveries } from './pages/Deliveries/Deliveries';
import { DeliveriesAttention } from './pages/DeliveriesAttention/DeliveriesAttention';
import { OrderDetails } from './pages/OrderDetails/OrderDetails';
import { Upload } from './pages/Upload/Upload';
import EcommerceConfiguration from './pages/Ecommerce/EcommerceConfiguration';
import EcommerceDetail from './pages/Ecommerce/EcommerceDetail';
import WebhookConfiguration from './pages/Webhooks/WebhookConfiguration';
import WebhookDetail from './pages/Webhooks/WebhookDetail';
import { CreatePickupRequest } from './pages/PickupRequests/CreatePickupRequest';
import { AssignToManifest } from './pages/PickupRequests/AssignToManifest';
import { WarehouseScope } from '../../layouts/WarehouseScope';
import { Inventory } from './pages/Inventory';
import { InventoryKardex } from './pages/InventoryKardex';
import { Movements } from './pages/Movements';
import { InventoryShopify } from './pages/InventoryShopify';
import { InventoryShopifySync } from './pages/InventoryShopifySync';
import { Locations } from './pages/Locations';
import { Products } from './pages/Products';
import { Putaway } from './pages/Putaway';
import { ReceiptDetail, Receipts } from './pages/Receipts';
import { Supplies, SupplyDetail } from './pages/Supplies';
import { WarehouseClients } from './pages/WarehouseClients';
import { WarehouseOrderDetail, WarehouseOrderFulfill, WarehouseOrders } from './pages/WarehouseOrders';

/**
 * The backend's OAuth callbacks redirect to `${FRONT_URL}configuration/ecommerce`,
 * and `FRONT_URL` still carries the legacy's `/home/`. Forwarded with its query,
 * so the return lands on the page whichever value the variable has.
 */
const LegacyEcommerceReturn = () => {
  const { search } = useLocation();
  return <Navigate to={`/configuration/ecommerce${search}`} replace />;
};

/**
 * Mounted by `AppRouter` when the session is a seller's. Same addresses as the
 * admin's where the section exists in both, so a link means the same thing to
 * either profile.
 */
export const sellerRoutes = (
  <Route element={<SellerLayout />}>
    <Route path="/home" element={<Navigate to="/dashboard" replace />} />
    <Route path="/dashboard" element={<Dashboard />} />
    <Route path="/deliveries" element={<Deliveries />} />
    <Route path="/deliveries/attention" element={<DeliveriesAttention />} />
    <Route path="/order-details/:orderId" element={<OrderDetails />} />
    <Route path="/upload" element={<Upload />} />
    <Route path="/account" element={<Account />} />
    <Route path="/configuration/ecommerce" element={<EcommerceConfiguration />} />
    <Route path="/configuration/ecommerce/new/:platform" element={<EcommerceDetail />} />
    <Route path="/configuration/ecommerce/:integrationId" element={<EcommerceDetail />} />
    <Route path="/home/configuration/ecommerce" element={<LegacyEcommerceReturn />} />
    <Route path="/configuration/webhooks" element={<WebhookConfiguration />} />
    <Route path="/configuration/webhooks/new" element={<WebhookDetail />} />
    <Route path="/configuration/webhooks/:webhookId" element={<WebhookDetail />} />
    <Route path="/pickup-requests/create" element={<CreatePickupRequest />} />
    <Route path="/pickup-requests/assign" element={<AssignToManifest />} />
    {/* The warehouse module, same addresses as the admin's. `WarehouseScope`
        answers for the addon. Preparing an order is reachable, and the screen
        itself opens only for an order in a warehouse the seller holds. */}
    <Route element={<WarehouseScope />}>
      {/* The module opens on the orders: the bare address lands there too. */}
      <Route path="/warehouse" element={<Navigate to={WAREHOUSE_HOME} replace />} />
      <Route path="/warehouse/orders" element={<WarehouseOrders />} />
      <Route path="/warehouse/orders/:orderId" element={<WarehouseOrderDetail />} />
      <Route path="/warehouse/orders/:orderId/fulfill" element={<WarehouseOrderFulfill />} />
      <Route path="/warehouse/receipts" element={<Receipts />} />
      <Route path="/warehouse/receipts/:receiptId" element={<ReceiptDetail />} />
      <Route path="/warehouse/putaway" element={<Putaway />} />
      <Route path="/warehouse/inventory" element={<Inventory />} />
      <Route path="/warehouse/inventory/shopify" element={<InventoryShopify />} />
      <Route path="/warehouse/inventory/shopify/sync" element={<InventoryShopifySync />} />
      <Route path="/warehouse/inventory/:productId" element={<InventoryKardex />} />
      <Route path="/warehouse/movements" element={<Movements />} />
      <Route path="/warehouse/products" element={<Products />} />
      <Route path="/warehouse/supplies" element={<Supplies />} />
      <Route path="/warehouse/supplies/:supplyId" element={<SupplyDetail />} />
      <Route path="/warehouse/locations" element={<Locations />} />
      <Route path="/warehouse/clients" element={<WarehouseClients />} />
    </Route>
    <Route path="*" element={<NotFound />} />
  </Route>
);
